/* eslint-disable indent, @typescript-eslint/indent, react/jsx-indent, react/jsx-indent-props */
import React from 'react';
import ZoomVideo, {
    VideoPlayer,
    VideoPlayerContainer,
    VideoQuality,
    LocalVideoTrack,
    ParticipantPropertiesPayload,
    Stream,
} from '@zoom/videosdk';
import s from './s.module.less';
import { message, Modal, Popover } from 'antd';
import { EMeetingUserType, EZoomErrorType, EZoomVideoViewActionType, TZoomVideoViewProps } from 'types/zoom';
import { providerUpdateVideoAudio, patientUpdateVideoAudio, reportError } from 'api/zoom';
import Logo from 'assets/logo/iconLogo.png';
import { ZoomParticipant } from './type';
import ZoomPIPView from './components/ZoomPIPView';
import { getUrlParam } from 'utils/common';
import VideoSettingModal from '../VideoSettingModal';

declare global {
    namespace JSX {
        interface IntrinsicElements {
            ['video-player']: React.DetailedHTMLProps<React.HTMLAttributes<VideoPlayer>, VideoPlayer> & {
                class?: string;
            };
            ['video-player-container']: React.DetailedHTMLProps<
                React.HTMLAttributes<VideoPlayerContainer>,
                VideoPlayerContainer
            > & { class?: string };
        }
    }
}

type JoinStatus = 'idle' | 'connecting' | 'joined' | 'error';

// 比例不合适， meetiong 中logo 显示不出来
const VideoImageUrl: string[] = [
    'https://cdn.helloklarity.net/ui/img/telehealth/20260112-094433.png',
    'https://cdn.helloklarity.net/ui/img/telehealth/20260112-094438.jpeg',
    'https://cdn.helloklarity.net/ui/img/telehealth/20260112-094444.jpeg',
    'https://cdn.helloklarity.net/ui/img/telehealth/20260112-094416.jpg',
];

const ZoomVideoView = (props: TZoomVideoViewProps) => {
    const {
        sessionKey,
        token,
        userName,
        mode = 'call',
        videoLayout = 'default',
        videoWidth,
        videoHeight,
        videoDefaultOn = false,
        audioDefaultOn = false,
        pipVideoWidth = 160,
        pipVideoHeight = 90,
        layout = 'default',
        role,
        onAction,
        userUuid,
        hidePreviewReminderAction = false,
        hideScheduleFollowUpAction = false,
        endFlag,
        newCommingMessage,
        newCommingMessageAmount,
        reLayoutWhenSharing = false,
        ratio = 9 / 16,
        syncAudioFlag,
        startFlag,
    } = props;

    const [status, setStatus] = React.useState<JoinStatus>('idle');
    const [error, setError] = React.useState<string | null>(null);

    const clientRef = React.useRef<ReturnType<typeof ZoomVideo.createClient> | null>(null);
    const mediaStreamRef = React.useRef<typeof Stream | null>(null);
    const mainVideoRef = React.useRef<VideoPlayer | null>(null); // 主窗口：显示对方的视频
    const othersSharingVideoRef = React.useRef<VideoPlayer | null>(null); // 其他窗口：显示其他人的视频
    const previewVideoRef = React.useRef<VideoPlayer | null>(null);
    const modalVideoRef = React.useRef<VideoPlayer | null>(null);
    const localVideoTrackRef = React.useRef<LocalVideoTrack | null>(null);
    const shareScreenVideoRef = React.useRef<HTMLVideoElement | null>(null);
    const [initing, setIniting] = React.useState<boolean>(true);
    const [showBackgroundModal, setShowBackgroundModal] = React.useState<boolean>(false);
    const [backgroundItemIndex, setBackgroundItemIndex] = React.useState<number>();
    const virtualBackground = React.useRef<undefined | { imageUrl: string | 'blur' | undefined; cropped?: boolean }>(
        undefined,
    );

    const [isVideoOn, setIsVideoOn] = React.useState<boolean>(videoDefaultOn);
    const [isAudioOn, setIsAudioOn] = React.useState<boolean>(audioDefaultOn);
    const isAudioOnRef = React.useRef<boolean>(audioDefaultOn);
    const [isSuccessStartAudio, setIsSuccessStartAudio] = React.useState<boolean>(false);
    const isSuccessStartAudioRef = React.useRef<boolean>(false);
    const isScreenSharing = React.useRef<boolean>(false);
    // 跟踪当前显示视频的用户 ID
    const currentMainUserIdRef = React.useRef<number | null>(null);
    const [currentMainUser, setCurrentMainUser] = React.useState<ZoomParticipant>();
    const [allMeetingUsers, setAllMeetingUsers] = React.useState<ZoomParticipant[]>([]);
    const currentSharingUseIdRef = React.useRef<number | null>(null);
    const [allUsersExceptMain, setAllUsersExceptMain] = React.useState<ZoomParticipant[]>([]);
    const [maxHeight, setMaxHeight] = React.useState<number>(0);
    const ohtersIsSharing = React.useRef<boolean>(false);
    const [showSettingModal, setShowSettingModal] = React.useState<boolean>(false);
    const hideAction = layout === 'float';
    const hasCallStopWithMouseMove = React.useRef<boolean>(false);

    const logError = (errorCode: EZoomErrorType, err: any) => {
        try {
            let errStr: string = '';

            // 处理 null 或 undefined
            if (err === null) {
                errStr = 'null';
            } else if (err === undefined) {
                errStr = 'undefined';
            } else if (err instanceof Error) {
                const errorInfo: Record<string, any> = {
                    name: err.name || 'Error',
                    message: err.message || '',
                };

                // 提取 stack 信息
                if (err.stack) {
                    errorInfo.stack = err.stack;
                }

                // 提取其他可能的错误属性（如 code, statusCode, status 等）
                if ('code' in err && err.code !== undefined) {
                    errorInfo.code = err.code;
                }
                if ('statusCode' in err && err.statusCode !== undefined) {
                    errorInfo.statusCode = err.statusCode;
                }
                if ('status' in err && err.status !== undefined) {
                    errorInfo.status = err.status;
                }
                if ('response' in err && err.response) {
                    // 处理 axios 等库的 response 对象
                    try {
                        errorInfo.response = {
                            status: (err.response as any).status,
                            statusText: (err.response as any).statusText,
                            data: (err.response as any).data,
                        };
                    } catch {
                        // 如果 response 对象无法序列化，跳过
                    }
                }

                // 尝试提取对象上的其他可枚举属性
                try {
                    const additionalProps: Record<string, any> = {};
                    for (const key in err) {
                        // eslint-disable-next-line no-prototype-builtins
                        if (
                            Object.prototype.hasOwnProperty.call(err, key) &&
                            !['name', 'message', 'stack', 'code', 'statusCode', 'status', 'response'].includes(key)
                        ) {
                            try {
                                const value = (err as any)[key];
                                // 跳过函数和循环引用
                                if (typeof value !== 'function') {
                                    additionalProps[key] = value;
                                }
                            } catch {
                                // 跳过无法访问的属性
                            }
                        }
                    }
                    if (Object.keys(additionalProps).length > 0) {
                        errorInfo.additionalProps = additionalProps;
                    }
                } catch {
                    // 忽略提取额外属性时的错误
                }

                // 使用安全的 JSON.stringify，处理可能的循环引用
                try {
                    errStr = JSON.stringify(errorInfo, null, 2);
                } catch {
                    // 如果 JSON.stringify 失败，使用手动拼接
                    errStr = `Error: ${errorInfo.name} - ${errorInfo.message}${errorInfo.stack ? `\nStack: ${errorInfo.stack}` : ''}`;
                }
            } else if (typeof err === 'object') {
                try {
                    // 使用 replacer 函数处理循环引用
                    const seen = new WeakSet();
                    errStr = JSON.stringify(
                        err,
                        (key, value) => {
                            // 跳过函数
                            if (typeof value === 'function') {
                                return '[Function]';
                            }
                            // 处理循环引用
                            if (typeof value === 'object' && value !== null) {
                                if (seen.has(value)) {
                                    return '[Circular]';
                                }
                                seen.add(value);
                            }
                            return value;
                        },
                        2,
                    );
                } catch (e) {
                    // 如果 JSON.stringify 仍然失败，尝试提取关键信息
                    try {
                        const errorObj: Record<string, any> = {};
                        for (const key in err) {
                            if (Object.prototype.hasOwnProperty.call(err, key)) {
                                try {
                                    const value = (err as any)[key];
                                    if (typeof value !== 'function') {
                                        errorObj[key] = value;
                                    }
                                } catch {
                                    errorObj[key] = '[无法访问]';
                                }
                            }
                        }
                        errStr = JSON.stringify(errorObj, null, 2);
                    } catch {
                        // 最后的兜底方案
                        errStr = `[Object: ${Object.prototype.toString.call(err)}]`;
                    }
                }
            } else if (typeof err === 'string') {
                errStr = err;
            } else if (typeof err === 'number' || typeof err === 'boolean') {
                errStr = String(err);
            } else if (typeof err === 'function') {
                errStr = `[Function: ${err.name || 'anonymous'}]`;
            } else {
                try {
                    errStr = String(err);
                    // 如果 toString 返回 [object Object]，尝试其他方法
                    if (errStr === '[object Object]') {
                        errStr = `[${typeof err}: ${Object.prototype.toString.call(err)}]`;
                    }
                } catch {
                    errStr = '[无法转换为字符串]';
                }
            }

            // 确保 errStr 不为空
            if (!errStr || errStr.trim() === '') {
                errStr = '[空错误信息]';
            }

            reportError({
                errorCode,
                message: errStr,
            });
        } catch (e: any) {
            // 如果整个解析过程都失败了，至少记录原始错误
            console.error('logError 解析失败:', e);
            console.error('原始错误:', err);
            try {
                reportError({
                    errorCode,
                    message: `[错误解析失败] 原始错误类型: ${typeof err}, 解析错误: ${e?.message || String(e)}`,
                });
            } catch {
                // 最后的兜底，直接输出到控制台
                console.error('reportError 也失败了，错误代码:', errorCode, '原始错误:', err);
            }
        }
    };

    //patient端的layout需求：https://www.figma.com/design/F7gBJjucXoAst1EsfaG8vC/Telehealth?node-id=1656-35970&m=dev

    // console.log({
    //     allMeetingUsers,
    //     allUsersExceptMain,
    //     currentMainUser,
    //     currentMainUserIdRef: currentMainUserIdRef.current,
    //     isScreenSharing: isScreenSharing.current,
    // });

    React.useEffect(() => {
        if (backgroundItemIndex && [1, 2, 3, 4, 5].includes(backgroundItemIndex)) {
            if (backgroundItemIndex === 1) {
                virtualBackground.current = {
                    imageUrl: 'blur',
                };
            } else {
                virtualBackground.current = {
                    imageUrl: VideoImageUrl[backgroundItemIndex - 2],
                    cropped: true,
                };
            }
        } else {
            virtualBackground.current = undefined;
        }
    }, [backgroundItemIndex]);

    React.useEffect(() => {
        const reloadImage = () => {
            VideoImageUrl.forEach((item) => {
                const img = new Image();
                img.src = item;
                img.onload = () => {
                    // console.log('img loaded', item);
                };
            });
        };
        reloadImage();
        try {
            const urlBackgroundItemIndex = getUrlParam('backgroundItemIndex');
            if (urlBackgroundItemIndex) {
                const _backgroundItemIndex = parseInt(urlBackgroundItemIndex, 10);
                if (_backgroundItemIndex && [1, 2, 3, 4, 5].includes(_backgroundItemIndex)) {
                    if (_backgroundItemIndex === 1) {
                        virtualBackground.current = {
                            imageUrl: 'blur',
                        };
                    } else {
                        virtualBackground.current = {
                            imageUrl: VideoImageUrl[_backgroundItemIndex - 2],
                            cropped: true,
                        };
                    }
                } else {
                    virtualBackground.current = undefined;
                }
            }
        } catch (e) {
            console.error(e);
        }
    }, []);

    React.useEffect(() => {
        if (currentMainUser && mediaStreamRef.current && mainVideoRef.current && currentMainUser.bVideoOn) {
            mediaStreamRef.current.attachVideo(currentMainUser.userId, VideoQuality.Video_720P, mainVideoRef.current);
        } else if (currentMainUser && mediaStreamRef.current && mainVideoRef.current && !currentMainUser.bVideoOn) {
            mediaStreamRef.current.detachVideo(currentMainUser.userId, mainVideoRef.current);
        }
    }, [currentMainUser]);
    //16 / 9
    let caculateVideoHeight = 'auto';
    let callWrapHeight = caculateVideoHeight;
    if (videoWidth) {
        caculateVideoHeight = `${videoWidth * ratio}px`;
        callWrapHeight = caculateVideoHeight;
        const vHeight = videoWidth * ratio;
        if (maxHeight && maxHeight < vHeight) {
            callWrapHeight = '100%';
        }
    }

    let sharingWidth = videoWidth;
    let sharingHeight = '100%';
    let pipWidthForUsing = pipVideoWidth;
    let pipHeightForUsing = pipWidthForUsing * ratio;

    if (reLayoutWhenSharing && ohtersIsSharing.current && videoWidth) {
        sharingWidth = videoWidth - 450;
        sharingHeight = `${sharingWidth * ratio}px`;
        sharingHeight = '100%';
        pipWidthForUsing = 420;
        pipHeightForUsing = 420 * ratio;
    }

    // console.log({
    //     reLayoutWhenSharing,
    //     ohtersIsSharing: ohtersIsSharing.current,
    //     videoWidth,
    //     sharingWidth,
    //     sharingHeight,
    // });

    React.useEffect(() => {
        if (!sessionKey || !userUuid) {
            return;
        }
        if (role === EMeetingUserType.PROVIDER) {
            providerUpdateVideoAudio(sessionKey, userUuid, isAudioOn, isVideoOn);
        } else {
            patientUpdateVideoAudio(sessionKey, userUuid, isAudioOn, isVideoOn);
        }
    }, [isVideoOn, isAudioOn, role, sessionKey, userUuid]);

    const startPreviewVideo = async () => {
        if (!localVideoTrackRef.current || !previewVideoRef.current) {
            return;
        }
        try {
            await localVideoTrackRef.current.start(previewVideoRef.current);
            setIsVideoOn(true);
        } catch (err) {
            console.error('Failed to start preview video:', err);
        }
    };

    const stopPreviewVideo = async () => {
        if (!localVideoTrackRef.current) {
            return;
        }
        try {
            await localVideoTrackRef.current.stop();
            setIsVideoOn(false);
        } catch (err) {
            console.error('Failed to stop preview video:', err);
        }
    };

    const startPreviewAudio = () => {
        // Preview 模式下音频只是状态标记
        setIsAudioOn(true);
    };

    const stopPreviewAudio = () => {
        // Preview 模式下音频只是状态标记
        setIsAudioOn(false);
    };

    const startCallVideo = async () => {
        console.log('startCallVideo');
        if (!mediaStreamRef.current) {
            return;
        }
        try {
            await mediaStreamRef.current.startVideo({
                hd: true,
                fullHd: true,
                ptz: mediaStreamRef?.current.isBrowserSupportPTZ(),
                virtualBackground: virtualBackground.current,
                // originalRatio: false,
                // captureWidth: 720,
                // captureHeight: 720 * ratio,
            });
            setIsVideoOn(true);
        } catch (err: unknown) {
            const videoError = err as { errorCode?: number; message?: string };
            // 如果视频已经启动（errorCode: 6109），认为操作成功
            if (videoError?.errorCode === 6109) {
                console.warn('Video is already started');
                setIsVideoOn(true);
            } else {
                // 其他错误才抛出
                console.error('Failed to start call video:', err);
                logError(EZoomErrorType.START_VIDEO, err);
                throw err;
            }
        }

        // 等待摄像头打开
        await new Promise((resolve) => {
            setTimeout(resolve, 500);
        });
    };

    const stopCallVideo = async () => {
        if (!mediaStreamRef.current) {
            return;
        }
        try {
            await mediaStreamRef.current.stopVideo();
            setIsVideoOn(false);
        } catch (err) {
            console.error('Failed to stop call video:', err);
            logError(EZoomErrorType.STOP_VIDEO, err);
        }
    };

    const startCallAudio = async (showNote: boolean = false) => {
        if (!mediaStreamRef.current) {
            return;
        }
        try {
            // 如果音频未启动，先启动
            if (!isSuccessStartAudio) {
                console.log('do mediaStream.startAudio');
                await mediaStreamRef.current.startAudio();
                console.log('do mediaStream.startAudio success');
                setIsSuccessStartAudio(true);
            }
            console.log('do mediaStream.unmuteAudio');
            await mediaStreamRef.current.unmuteAudio();
            console.log('do mediaStream.unmuteAudio success');
            setIsAudioOn(true);
            if (showNote) {
                message.success('Audio unmuted.');
            }
        } catch (err) {
            console.error('Failed to start call audio:', err);
            logError(EZoomErrorType.START_AUDIO, err);
        }
    };

    const stopCallAudio = async (showNote: boolean = false) => {
        if (!mediaStreamRef.current) {
            return;
        }
        try {
            console.log({
                isSuccessStartAudio,
            });
            // 先检查音频是否已启动
            if (!isSuccessStartAudio) {
                console.log('do mediaStream.startAudio');
                await mediaStreamRef.current.startAudio();
                console.log('do mediaStream.startAudio success');
                setIsSuccessStartAudio(true);
            }
            console.log('do mediaStream.muteAudio');
            await mediaStreamRef.current.muteAudio();
            console.log('do mediaStream.muteAudio success');
            setIsAudioOn(false);
            if (showNote) {
                message.success('Audio muted');
            }
        } catch (err) {
            console.error('Failed to stop call audio:', err);
            logError(EZoomErrorType.STOP_AUDIO, err);
        }
    };

    const startShareScreen = async () => {
        if (currentSharingUseIdRef.current) {
            message.error('Someone is sharing the screen');
            return;
        }
        if (!shareScreenVideoRef.current || !mediaStreamRef.current) {
            return;
        }
        console.log('do mediaStream.startShareScreen');
        await mediaStreamRef.current.startShareScreen(shareScreenVideoRef.current);
        console.log('do mediaStream.startShareScreen success');
        currentSharingUseIdRef.current = clientRef.current?.getCurrentUserInfo()?.userId!;
        currentMainUserIdRef.current = currentSharingUseIdRef.current;
        isScreenSharing.current = true;
        message.success('Screen sharing started.');
    };

    const stopShareScreen = async () => {
        console.log('stopShareScreen');
        if (!mediaStreamRef.current) {
            return;
        }
        console.log('do mediaStream.stopShareScreen');
        await mediaStreamRef.current.stopShareScreen();
        console.log('do mediaStream.stopShareScreen success');
        isScreenSharing.current = false;
    };

    const handleSetBackground = async (inx: number) => {
        setBackgroundItemIndex(inx);
        if (mode === 'call' && mediaStreamRef.current) {
            // mediaStream 可以？
            if (inx === 0) {
                await mediaStreamRef.current?.updateVirtualBackgroundImage(undefined);
            } else if (inx === 1) {
                await mediaStreamRef.current?.updateVirtualBackgroundImage('blur');
            } else {
                const imageUrl = VideoImageUrl[inx - 2];
                await mediaStreamRef.current?.updateVirtualBackgroundImage(imageUrl);
            }
        }
    };

    React.useEffect(() => {
        isAudioOnRef.current = isAudioOn;
    }, [isAudioOn]);

    React.useEffect(() => {
        isSuccessStartAudioRef.current = isSuccessStartAudio;
    }, [isSuccessStartAudio]);

    React.useEffect(() => {
        const _startCallAudio = async () => {
            if (!mediaStreamRef.current) {
                return;
            }
            try {
                // 如果音频未启动，先启动
                if (!isSuccessStartAudioRef.current) {
                    console.log('do mediaStream.startAudio');
                    await mediaStreamRef.current.startAudio();
                    console.log('do mediaStream.startAudio success');
                    setIsSuccessStartAudio(true);
                }
                console.log('do mediaStream.unmuteAudio');
                await mediaStreamRef.current.unmuteAudio();
                console.log('do mediaStream.unmuteAudio success');
                setIsAudioOn(true);
            } catch (err) {
                console.error('Failed to start call audio:', err);
                logError(EZoomErrorType.START_AUDIO, err);
            }
        };
        const _stopCallAudio = async () => {
            if (!mediaStreamRef.current) {
                return;
            }
            try {
                // 先检查音频是否已启动
                if (!isSuccessStartAudioRef.current) {
                    console.log('do mediaStream.startAudio');
                    await mediaStreamRef.current.startAudio();
                    console.log('do mediaStream.startAudio success');
                    setIsSuccessStartAudio(true);
                }
                console.log('do mediaStream.muteAudio');
                await mediaStreamRef.current.muteAudio();
                console.log('do mediaStream.muteAudio success');
                setIsAudioOn(false);
            } catch (err) {
                console.error('Failed to stop call audio:', err);
                logError(EZoomErrorType.STOP_AUDIO, err);
            }
        };
        if (syncAudioFlag) {
            if (isAudioOnRef.current) {
                _startCallAudio();
            } else {
                _stopCallAudio();
            }
        }
    }, [syncAudioFlag]);

    React.useEffect(() => {
        if (mode !== 'preview' || !localVideoTrackRef.current) {
            return;
        }

        const switchVideoElement = async () => {
            try {
                // 先停止当前的视频轨道
                if (localVideoTrackRef.current) {
                    try {
                        await localVideoTrackRef.current.stop();
                    } catch (err: any) {
                        // 如果视频未启动（VideoNotStartedError），可以忽略
                        if (err?.name !== 'VideoNotStartedError' && !err?.message?.includes('VideoNotStarted')) {
                            console.error('Failed to stop local video track:', err);
                        }
                    }
                }

                // 等待一下确保停止完成
                await new Promise<void>((resolve) => {
                    setTimeout(resolve, 200);
                });

                setTimeout(() => {
                    if (showBackgroundModal) {
                        if (modalVideoRef.current && localVideoTrackRef.current) {
                            localVideoTrackRef.current.start(modalVideoRef.current);
                        }
                    } else if (previewVideoRef.current && localVideoTrackRef.current) {
                        localVideoTrackRef.current.start(previewVideoRef.current);
                    }
                }, 600);
                // 根据 showBackgroundModal 状态在对应的元素上启动视频
            } catch (err) {
                console.error('Failed to switch video element:', err);
            }
        };

        switchVideoElement();
    }, [showBackgroundModal, mode]);

    const cleanup = async () => {
        if (mode === 'call' && clientRef.current) {
            try {
                if (isVideoOn) {
                    await stopCallVideo();
                }
                if (isAudioOn) {
                    console.log('call stopCallAudio from cleanup');
                    await stopCallAudio();
                }
                console.log('call clientRef leave');
                await clientRef.current.leave();
                await ZoomVideo.destroyClient();
                // 等待离开完成
                await new Promise((resolve) => {
                    setTimeout(resolve, 200);
                });
                onAction?.(EZoomVideoViewActionType.CALL_IS_ENDED);
            } catch (err) {
                console.error('Failed to cleanup call:', err);
                await new Promise((resolve) => {
                    setTimeout(resolve, 500);
                });
                onAction?.(EZoomVideoViewActionType.CALL_IS_ENDED);
            }
        } else if (mode === 'preview') {
            try {
                // Preview 模式：停止预览并释放摄像头资源
                if (localVideoTrackRef.current) {
                    // 1. 停止本地视频轨道（如果已启动）
                    try {
                        await localVideoTrackRef.current.stop();
                    } catch (err: unknown) {
                        // 如果视频未启动（VideoNotStartedError），忽略错误
                        const stopError = err as {
                            name?: string;
                            message?: string;
                        };
                        if (
                            stopError?.name === 'VideoNotStartedError' ||
                            stopError?.message?.includes('VideoNotStarted')
                        ) {
                            // 视频未启动，无需停止，继续清理
                        } else {
                            // 其他错误才记录警告
                            console.warn('Failed to stop local video track:', err);
                        }
                    }

                    // 2. 清理 video element 的 srcObject（如果存在）
                    if (previewVideoRef.current) {
                        try {
                            const videoElement = previewVideoRef.current as any;
                            if (videoElement?.srcObject) {
                                const stream = videoElement.srcObject as MediaStream;
                                stream.getTracks().forEach((track) => {
                                    track.stop();
                                });
                                videoElement.srcObject = null;
                            }
                        } catch (err) {
                            // 忽略清理 video element 的错误
                        }
                    }

                    // 3. 等待足够的时间让浏览器和 Zoom SDK 释放摄像头资源
                    await new Promise((resolve) => {
                        setTimeout(resolve, 1000);
                    });

                    localVideoTrackRef.current = null;
                }
                // 销毁 client（preview 模式也需要清理）
                if (clientRef.current) {
                    ZoomVideo.destroyClient();
                    // 等待 client 销毁完成
                    await new Promise((resolve) => {
                        setTimeout(resolve, 300);
                    });
                }
            } catch (ex) {
                console.error('Failed to cleanup call:', ex);
                // 即使清理失败，也等待一段时间让资源释放
                await new Promise((resolve) => {
                    setTimeout(resolve, 500);
                });
                onAction?.(EZoomVideoViewActionType.CALL_IS_ENDED);
            }
        }
    };

    React.useEffect(() => {
        return () => {
            cleanup();
        };
    }, []);

    React.useEffect(() => {
        if (endFlag) {
            cleanup();
        }
    }, [endFlag]);

    React.useEffect(() => {
        const wrapRef = document.getElementById('zoom-video-view');
        if (wrapRef) {
            setMaxHeight(wrapRef.clientHeight);
        }
    }, [videoWidth]);

    React.useEffect(() => {
        const cancelled = false;
        const initPreview = async () => {
            // Preview 模式：使用 Zoom SDK 的预览功能，不加入会议
            // 需要先初始化 SDK（但不 join），然后才能使用预览功能
            if (!clientRef.current) {
                clientRef.current = ZoomVideo.createClient();
            }
            const client = clientRef.current;

            // 初始化 SDK（但不 join）
            await client.init('en-US', 'https://source.zoom.us/videosdk/2.3.0/lib', {
                webEndpoint: 'zoom.us',
                leaveOnPageUnload: true,
                stayAwake: true,
                patchJsMedia: true,
            });

            // 等待 DOM 元素准备好
            // 使用 setTimeout 确保 ref 已经设置
            await new Promise((resolve) => {
                const checkRef = () => {
                    if (previewVideoRef.current) {
                        resolve(undefined);
                    } else {
                        setTimeout(checkRef, 50);
                    }
                };
                checkRef();
            });

            if (cancelled) return;

            // 创建本地视频轨道
            const localVideoTrack = ZoomVideo.createLocalVideoTrack();
            localVideoTrackRef.current = localVideoTrack;

            // 根据 videoDefaultOn 标志决定是否启动视频
            if (videoDefaultOn && previewVideoRef.current) {
                await localVideoTrack.start(previewVideoRef.current);
            }

            setStatus('joined');
        };

        const handleUserUpdate = async (payload: ParticipantPropertiesPayload[]) => {
            console.log('handleUserUpdate');
            const mediaStream = mediaStreamRef.current;
            const client = clientRef.current;
            if (!mediaStream || !client || !mainVideoRef.current) {
                return;
            }
            const currentUser = client.getCurrentUserInfo();
            const allUsers = client.getAllUser() as ZoomParticipant[];
            console.log({
                isScreenSharing: isScreenSharing.current,
                currentMainUserIdRef: currentMainUserIdRef.current,
                allUsers,
            });
            if (isScreenSharing.current) {
                //停止状态只能从zoom那里获取
                if (currentMainUserIdRef.current) {
                    const currentMainUserInfo = allUsers.find((user) => user.userId === currentMainUserIdRef.current);
                    if (currentMainUserInfo) {
                        if (!currentMainUserInfo.sharerOn && currentMainUserIdRef.current === currentUser?.userId) {
                            stopShareScreen();
                        }
                    }
                }
            }
            setAllMeetingUsers(allUsers);

            const onlyOneUser = allUsers.length === 1;
            if (onlyOneUser) {
                const user = allUsers[0];
                setCurrentMainUser(user);
                setAllUsersExceptMain([]);
                if (user.bVideoOn && mainVideoRef.current && !isScreenSharing.current) {
                    try {
                        await mediaStream.attachVideo(user.userId, VideoQuality.Video_720P, mainVideoRef.current);
                        currentMainUserIdRef.current = user.userId;
                    } catch (err) {
                        console.error('Failed to attach video:', err);
                    }
                } else if (mainVideoRef.current && !isScreenSharing.current) {
                    mediaStream.detachVideo(user.userId, mainVideoRef.current);
                }
            } else {
                const sharingUser = allUsers.find((user) => user.sharerOn === true);
                console.log({
                    sharingUser,
                    currentUser,
                    allUsers,
                    currentSharingUseIdRef: currentSharingUseIdRef.current,
                });
                if (sharingUser) {
                    setAllUsersExceptMain(allUsers);
                    if (sharingUser.userId !== currentUser?.userId) {
                        // 有人开始屏幕共享
                        if (currentSharingUseIdRef.current !== sharingUser.userId) {
                            // 切换屏幕共享源
                            if (currentMainUserIdRef.current && shareScreenVideoRef.current) {
                                await mediaStream.detachShareView(
                                    currentMainUserIdRef.current,
                                    othersSharingVideoRef.current!,
                                );
                                ohtersIsSharing.current = false;
                                currentSharingUseIdRef.current = null;
                            }
                        }
                        try {
                            console.log(`attachShareView:${sharingUser.userId}`);
                            await mediaStream.attachShareView(sharingUser.userId, othersSharingVideoRef.current!);
                            ohtersIsSharing.current = true;
                        } catch (e) {
                            console.log(e);
                        }
                        currentSharingUseIdRef.current = sharingUser.userId;
                        setCurrentMainUser(sharingUser);
                        setAllUsersExceptMain(allUsers);
                    } else if (sharingUser.userId === currentUser?.userId) {
                        //i am sharing screen
                        //do nothing
                    }
                    return;
                }

                if (!sharingUser && currentSharingUseIdRef.current) {
                    await mediaStream.detachShareView(currentSharingUseIdRef.current, othersSharingVideoRef.current!);
                    ohtersIsSharing.current = false;
                    currentSharingUseIdRef.current = null;
                }

                let mainUser = allUsers.find((user) => user.userId !== currentUser?.userId && user.bVideoOn);
                if (!mainUser) {
                    mainUser = allUsers.find((user) => user.userId !== currentUser?.userId);
                }
                console.log({
                    mainUser,
                });
                if (mainUser) {
                    currentMainUserIdRef.current = mainUser.userId;
                    if (mainUser.bVideoOn) {
                        console.log('attachVideo mainUser', mainUser);
                        try {
                            if (currentSharingUseIdRef.current) {
                                await mediaStream.detachShareView(
                                    currentSharingUseIdRef.current,
                                    othersSharingVideoRef.current!,
                                );
                                ohtersIsSharing.current = false;
                                currentSharingUseIdRef.current = null;
                            }
                            await mediaStream.attachVideo(
                                mainUser.userId,
                                VideoQuality.Video_720P,
                                mainVideoRef.current,
                            );
                            currentMainUserIdRef.current = mainUser?.userId!;
                            setCurrentMainUser(mainUser);
                            //todo
                            //setAllUsersExceptMain(allUsers);
                            setAllUsersExceptMain(
                                allUsers.filter((user) => user.userId !== currentMainUserIdRef?.current),
                            );
                        } catch (err) {
                            console.error('Failed to attach video:', err);
                        }
                    } else {
                        setCurrentMainUser(mainUser);
                        try {
                            if (currentMainUserIdRef.current) {
                                await mediaStream.detachVideo(currentMainUserIdRef.current, mainVideoRef.current);
                            }
                        } catch (err) {
                            console.error('Failed to detach video:', err);
                        }
                        setAllUsersExceptMain(allUsers.filter((user) => user.userId !== currentMainUserIdRef?.current));
                    }
                }
            }
        };

        const initCall = async () => {
            if (!userName) {
                return;
            }
            // Call 模式：初始化 client 并加入会议
            if (!clientRef.current) {
                clientRef.current = ZoomVideo.createClient();
            }
            const client = clientRef.current;

            // 1. 初始化 SDK
            // 这里直接使用 Zoom 的 CDN 静态资源，不需要你复制 public/lib
            await client.init('en-US', 'https://source.zoom.us/videosdk/2.3.0/lib', {
                webEndpoint: 'zoom.us',
                leaveOnPageUnload: false,
                stayAwake: true,
                patchJsMedia: true,
            });

            // 2. 加入会话：直接使用你后端返回的 token
            try {
                console.log({
                    sessionKey,
                    token,
                    userName,
                });
                await client.join(sessionKey!, token!, userName!);
            } catch (e) {
                console.error(e);
                logError(EZoomErrorType.JOIN_CALL, e);
            }

            const mediaStream = client.getMediaStream();
            mediaStreamRef.current = mediaStream;
            // 监听参与者加入事件
            // 注意：根据 Zoom SDK 文档，payload 是 Array<ParticipantPropertiesPayload>
            client.on('user-added', (payload: ParticipantPropertiesPayload[]) => {
                console.log('User added:', payload);
                handleUserUpdate(payload);

                // 处理所有新加入的用户
            });

            // 监听参与者更新事件（视频开启/关闭）
            // 注意：根据 Zoom SDK 文档，payload 是 Array<ParticipantPropertiesPayload>
            // 但实际使用中可能包含 action 字段，需要检查 bVideoOn 的变化
            client.on('user-updated', (payload: ParticipantPropertiesPayload[]) => {
                console.log('User updated:', payload);
                handleUserUpdate(payload);
            });

            client.on('user-removed', (payload: ParticipantPropertiesPayload[]) => {
                console.log('User removed:', payload);
                handleUserUpdate(payload);
            });

            // 释放前一个页面可能占用的摄像头资源
            const releaseCameraResources = async () => {
                try {
                    console.log('Releasing camera resources from previous page...');

                    // 方法1: 尝试通过当前 Zoom SDK 实例停止可能存在的视频
                    // 注意：此时 mediaStreamRef.current 已经设置（在 client.join 之后）
                    if (mediaStreamRef.current) {
                        try {
                            // 尝试停止视频（如果已启动）
                            await mediaStreamRef.current.stopVideo();
                            console.log('Stopped existing video stream from current mediaStream');
                            // 等待视频停止完成
                            await new Promise((resolve) => {
                                setTimeout(resolve, 300);
                            });
                        } catch (err: unknown) {
                            const stopError = err as {
                                errorCode?: number;
                                message?: string;
                            };
                            // 如果视频未启动（errorCode 可能是其他值），忽略错误
                            // 6109 表示视频已启动，但我们尝试停止它，所以这个错误可以忽略
                            if (stopError?.errorCode && stopError.errorCode !== 6109) {
                                console.warn('Error stopping video:', err);
                            }
                        }
                    }

                    // 方法2: 尝试通过浏览器 API 获取并停止所有活动的视频轨道
                    // 注意：这只能停止当前页面的媒体流，无法访问其他页面的流
                    // 但可以确保当前页面的所有视频轨道都被停止
                    if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
                        try {
                            // 获取所有媒体设备（用于调试）
                            const devices = await navigator.mediaDevices.enumerateDevices();
                            const videoDevices = devices.filter((device) => device.kind === 'videoinput');
                            console.log(`Found ${videoDevices.length} video input devices`);
                        } catch (err) {
                            console.warn('Failed to enumerate devices:', err);
                        }
                    }

                    // 方法3: 等待足够的时间让浏览器和 Zoom SDK 释放资源
                    // 这是最重要的步骤，因为资源释放是异步的
                    await new Promise((resolve) => {
                        setTimeout(resolve, 1000);
                    });

                    console.log('Camera resources release completed');
                } catch (err) {
                    console.warn('Error releasing camera resources:', err);
                    // 即使释放失败，也继续执行，因为可能没有资源被占用
                    // 等待一段时间后继续
                    await new Promise((resolve) => {
                        setTimeout(resolve, 500);
                    });
                }
            };

            // 启动视频, 根据videoDefaultOn 标志决定是否attach
            // 添加重试机制处理摄像头资源竞争问题（errorCode: 6105）
            const startVideoWithRetry = async (maxRetries = 3, delay = 500) => {
                // eslint-disable-next-line no-await-in-loop
                for (let i = 0; i < maxRetries; i++) {
                    try {
                        // eslint-disable-next-line no-await-in-loop
                        await mediaStream.startVideo({
                            hd: true,
                            fullHd: true,
                            ptz: mediaStream?.isBrowserSupportPTZ(),
                            virtualBackground: virtualBackground.current,
                            // originalRatio: false,
                            // captureWidth: 720,
                            // captureHeight: 720 * ratio,
                        });
                        setIsVideoOn(true);
                        console.log('startVideoWithRetry success');
                        return; // 成功启动，退出重试循环
                    } catch (err: unknown) {
                        console.error(err);
                        logError(EZoomErrorType.START_VIDEO, err);
                        const videoError = err as {
                            errorCode?: number;
                            message?: string;
                        };
                        // 如果视频已经启动（errorCode: 6109），说明摄像头可能被占用
                        // 先尝试释放资源，然后重试
                        if (videoError?.errorCode === 6109) {
                            console.warn('Video is already started, releasing resources and retrying...');
                            // 尝试释放资源
                            // eslint-disable-next-line no-await-in-loop
                            await releaseCameraResources();
                            // 如果还有重试次数，继续重试
                            if (i < maxRetries - 1) {
                                // eslint-disable-next-line no-await-in-loop
                                await new Promise((resolve) => {
                                    setTimeout(resolve, delay * (i + 1));
                                });
                                continue; // 继续重试
                            } else {
                                // 达到最大重试次数，抛出错误
                                console.error('Failed to start video after releasing resources');
                                logError(EZoomErrorType.START_VIDEO, err);
                                throw err;
                            }
                        }
                        // 检查是否是摄像头正在启动的错误（errorCode: 6105）
                        if (videoError?.errorCode === 6105 && i < maxRetries - 1) {
                            console.warn(`Camera is starting, retrying... (${i + 1}/${maxRetries})`);
                            // 等待后重试，每次重试延迟递增
                            // eslint-disable-next-line no-await-in-loop
                            await new Promise((resolve) => {
                                setTimeout(resolve, delay * (i + 1));
                            });
                        } else {
                            // 其他错误或达到最大重试次数
                            console.error('Failed to start video:', err);
                            logError(EZoomErrorType.START_VIDEO, err);
                            throw err;
                        }
                    }
                }
            };

            try {
                // 先释放前一个页面可能占用的摄像头资源
                await releaseCameraResources();
                // 再启动视频
                if (videoDefaultOn) {
                    await startVideoWithRetry();
                }
            } catch (err) {
                console.error('Failed to start video after retries:', err);
                logError(EZoomErrorType.START_VIDEO, err);
            }
            await new Promise((resolve) => {
                setTimeout(resolve, 500);
            });
            console.log('handle audio:', audioDefaultOn);
            // 根据 audioDefaultOn 标志决定是否启动音频
            let audioStarted = false;
            try {
                // 启动音频，增加 3 秒超时保护，避免 SDK 长时间无响应卡死流程
                console.log('do mediaStream.startAudio');
                const startAudioPromise = mediaStream.startAudio();
                await Promise.race([
                    startAudioPromise,
                    new Promise((_, reject) => {
                        setTimeout(() => reject(new Error('startAudio timeout (3s)')), 3000);
                    }),
                ]);
                console.log('do mediaStream.startAudio success');
                setIsSuccessStartAudio(true);
                audioStarted = true;
            } catch (e) {
                setIsAudioOn(false);
                console.warn('Failed to start audio:', e);
            }
            if (audioStarted && audioDefaultOn) {
                try {
                    console.log('do mediaStream.unmuteAudio');
                    await mediaStream.unmuteAudio();
                    console.log('do mediaStream.unmuteAudio success');
                    setIsAudioOn(true);
                } catch (e) {
                    console.warn('Failed to unmute audio:', e);
                }
            } else {
                try {
                    console.log('do mediaStream.muteAudio');
                    await mediaStream.muteAudio();
                    console.log('do mediaStream.muteAudio success');
                    setIsAudioOn(false);
                } catch (e) {
                    console.warn('Failed to mute audio:', e);
                    // setTimeout(() => {
                    //     try {
                    //         console.log("call stopCallAudio from timeout")
                    //         stopCallAudio();
                    //     } catch (er) {
                    //         console.error(er);
                    //     }
                    // }, 1000);
                }
            }

            // 处理已存在的参与者（在加入会议前就已经在会议中的用户）
            const allUsers = client.getAllUser() as ZoomParticipant[];
            console.log('initCall allUsers', allUsers);
            setStatus('joined');
            handleUserUpdate(allUsers);
        };

        const initMedia = async () => {
            try {
                setStatus('connecting');
                setError(null);

                if (mode === 'preview') {
                    initPreview();
                }
                if (mode === 'call' && sessionKey && token && userName && startFlag) {
                    console.log('init Call');
                    initCall();
                }
            } catch (e) {
                console.error('Failed to initialize media:', e);
                if (cancelled) return;
                setStatus('error');
                const errorMsg = mode === 'preview' ? 'Failed to start video preview' : 'Failed to join Zoom session';
                setError((e as Error)?.message || errorMsg);
            }
        };

        initMedia();
    }, [sessionKey, token, userName, mode, videoDefaultOn, audioDefaultOn, startFlag]);

    // 组件卸载时的清理逻辑（只在页面离开时执行一次）
    React.useEffect(() => {
        return () => {
            // 使用异步函数确保清理操作按顺序执行
            cleanup();
        };
    }, []); // 空依赖数组，只在组件卸载时执行一次

    let statusText: string | null = null;
    if (status === 'idle') {
        statusText = 'Preparing Zoom...';
    } else if (status === 'connecting') {
        statusText = 'Connecting to Zoom...';
    } else if (status === 'error') {
        statusText = error || 'Failed to join Zoom session';
    }

    const handleClickAudio = async () => {
        if (mode === 'preview') {
            // Preview 模式：只改变状态标记
            if (isAudioOn) {
                stopPreviewAudio();
                setIsAudioOn(false);
                message.success('Audio muted');
            } else {
                startPreviewAudio();
                setIsAudioOn(true);
                message.success('Audio unmuted');
            }
            return;
        }
        if (mode === 'call') {
            if (isAudioOn) {
                // Call 模式：控制实际音频 - 停止
                console.log('call stopCallAudio from handleClickAudio');
                await stopCallAudio(true);
            } else {
                // Call 模式：控制实际音频 - 启动
                await startCallAudio(true);
            }
        }
    };

    const handleClickVideo = async () => {
        if (mode === 'preview') {
            // Preview 模式
            if (isVideoOn) {
                await stopPreviewVideo();
                message.success('Video stopped');
            } else {
                await startPreviewVideo();
                message.success('Video started');
            }
        } else if (isVideoOn) {
            // Call 模式 - 停止
            await stopCallVideo();
            message.success('Video stopped');
        } else {
            // Call 模式 - 启动
            await startCallVideo();
            message.success('Video started');
        }
    };

    const handleClickShare = async () => {
        // Preview 模式：屏幕共享需要在加入会议后才能使用
        if (mode === 'preview') {
            return;
        }

        // Call 模式：使用 mediaStream 控制屏幕共享
        if (!mediaStreamRef.current) {
            return;
        }

        try {
            if (isScreenSharing.current) {
                stopShareScreen();
            } else {
                startShareScreen();
            }
        } catch (err) {
            console.error('Failed to toggle screen share:', err);
            // 如果启动失败，确保状态正确
            isScreenSharing.current = false;
        }
    };
    const handleClickChat = async () => {
        onAction?.(EZoomVideoViewActionType.MESSAGE);
    };
    const handleClickSendReminder = async () => {
        onAction?.(EZoomVideoViewActionType.SEND_A_REMINDER);
    };
    const handleClickScheduleFollowUp = async () => {
        onAction?.(EZoomVideoViewActionType.SCHEDULE_FOLLOW_UP);
    };
    const handleClickParticipants = async () => {
        onAction?.(EZoomVideoViewActionType.PARTICIPANTS);
    };

    const handleClickBackground = async () => {
        // await mediaStreamRef.current?.updateVirtualBackgroundImage('blur');
        // await mediaStreamRef.current?.updateVirtualBackgroundImage(
        //   ImageUrl[0]
        // );
        setShowBackgroundModal(true);
    };

    const handleClickSetting = async () => {
        setShowSettingModal(true);
    };

    const handleClickEnd = async () => {
        console.log(clientRef.current?.getAllUser());
        onAction?.(EZoomVideoViewActionType.END_CALL);
    };

    const hasOthers = allUsersExceptMain && allUsersExceptMain.length > 0;
    return (
        <div className={`${s.wrap} ${layout === 'float' ? s.wrapFloat : ''}`} id="zoom-video-view">
            {statusText && <div className={s.status}>{statusText}</div>}
            {mode === 'preview' && (
                // Preview 模式：使用 Zoom SDK 的 video-player（支持虚拟背景等功能）
                <div
                    className={`${s.previewWrap} ${!isVideoOn ? s.previewWrapEmpty : ''}`}
                    style={{ minHeight: caculateVideoHeight }}
                >
                    <video-player-container
                        className={s.videoContainer}
                        style={{
                            width: videoWidth ? `${videoWidth}px` : '100%',
                            height: caculateVideoHeight,
                        }}
                    >
                        <video-player
                            className={s.videoPlayer}
                            style={{
                                width: videoWidth ? `${videoWidth}px` : '100%',
                                height: caculateVideoHeight,
                            }}
                            ref={(el) => {
                                previewVideoRef.current = el;
                            }}
                        />
                    </video-player-container>
                    {(showBackgroundModal || !isVideoOn) && (
                        <div
                            className={s.placeHolder}
                            style={{
                                width: videoWidth ? `${videoWidth}px` : '100%',
                                height: caculateVideoHeight,
                            }}
                        >
                            <div className={s.holderName}>{userName}</div>
                            <img
                                src={Logo}
                                alt="logo"
                                width={layout === 'float' ? 40 : 80}
                                height={layout === 'float' ? 20 : 40}
                                className={s.logo}
                            />
                        </div>
                    )}
                </div>
            )}
            {mode === 'call' && (
                <div
                    className={`${s.callWrap} ${!isVideoOn ? s.callWrapEmpty : ''} ${
                        reLayoutWhenSharing && ohtersIsSharing.current ? s.callWrapReLayoutWhenSharing : ''
                    }`}
                    style={{
                        width: videoWidth ? `${videoWidth}px` : '100%',
                        height: callWrapHeight,
                    }}
                >
                    {/* 主窗口：显示对方的视频 */}
                    <div className={ohtersIsSharing.current ? s.videoContainerOthersSharing : ''}>
                        <video-player-container
                            className={s.videoContainer}
                            style={{
                                width: videoWidth ? `${videoWidth}px` : '100%',
                                height: caculateVideoHeight,
                            }}
                        >
                            <video-player
                                className={s.videoPlayer}
                                style={{
                                    width: videoWidth ? `${videoWidth}px` : '100%',
                                    height: caculateVideoHeight,
                                }}
                                ref={(el) => {
                                    mainVideoRef.current = el;
                                }}
                            />
                        </video-player-container>
                    </div>
                    {layout === 'float' && currentMainUser && currentMainUser.displayName && isVideoOn && (
                        <div className={s.floatName}>{currentMainUser.displayName}</div>
                    )}
                    {currentMainUser &&
                        !currentMainUser?.bVideoOn &&
                        !currentMainUser?.sharerOn &&
                        !isScreenSharing.current && (
                            <div
                                className={s.placeHolder}
                                style={{
                                    width: videoWidth ? `${videoWidth}px` : '100%',
                                    height: caculateVideoHeight,
                                }}
                            >
                                <img src={Logo} alt="logo" width={80} height={40} className={s.logo} />
                                <div className={s.holderName}>{currentMainUser.displayName}</div>
                            </div>
                        )}
                    <div className={`${s.othersSharing} ${ohtersIsSharing.current ? s.othersSharingShow : ''}`}>
                        <video-player-container
                            className={s.videoContainer}
                            style={{
                                width: sharingWidth ? `${sharingWidth}px` : '100%',
                                height: sharingHeight,
                            }}
                        >
                            <video-player
                                className={s.videoPlayer}
                                style={{
                                    width: sharingWidth ? `${sharingWidth}px` : '100%',
                                    height: sharingHeight,
                                }}
                                ref={(el) => {
                                    othersSharingVideoRef.current = el;
                                }}
                            />
                        </video-player-container>
                    </div>
                    {hasOthers && layout === 'picture-in-picture' && (
                        <div
                            className={s.othersList}
                            style={{
                                width: pipWidthForUsing ? `${pipWidthForUsing}px` : '160px',
                            }}
                        >
                            {allUsersExceptMain.map((user) => {
                                return (
                                    <div
                                        className={s.othersItem}
                                        key={user.userId}
                                        style={{
                                            width: pipWidthForUsing ? `${pipWidthForUsing}px` : '160px',
                                            height: pipHeightForUsing ? `${pipHeightForUsing}px` : '90px',
                                        }}
                                    >
                                        <ZoomPIPView
                                            mediaStreamRef={mediaStreamRef}
                                            zoomParticipant={user}
                                            width={pipWidthForUsing}
                                            height={pipHeightForUsing}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {/* Video element for screen sharing (required by WebCodecs) */}
                    {/* Video element for screen sharing (required by WebCodecs) */}
                    <video
                        ref={shareScreenVideoRef}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: isScreenSharing.current ? 'block' : 'none',
                        }}
                        autoPlay
                        playsInline
                    >
                        <track kind="captions" />
                    </video>
                </div>
            )}
            {mode === 'preview' && localVideoTrackRef.current && previewVideoRef.current && (
                <div className={s.actionWrap}>
                    <Popover
                        content={isAudioOn ? 'Stop audio' : 'Start audo'}
                        arrow={false}
                        trigger="hover"
                        rootClassName={s.pop}
                    >
                        <div
                            className={`${s.actionItem} ${isAudioOn ? '' : s.actionItemOff}`}
                            onClick={handleClickAudio}
                        >
                            <div className={s.actionItemIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="19"
                                    viewBox="0 0 14 19"
                                    fill="none"
                                >
                                    <path
                                        d="M7 12C8.66 12 9.99 10.66 9.99 9L10 3C10 1.34 8.66 0 7 0C5.34 0 4 1.34 4 3V9C4 10.66 5.34 12 7 12ZM12.3 9C12.3 12 9.76 14.1 7 14.1C4.24 14.1 1.7 12 1.7 9H0C0 12.41 2.72 15.23 6 15.72V19H8V15.72C11.28 15.24 14 12.42 14 9H12.3Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <span className={s.closeIcon} />
                            </div>
                            Audio
                        </div>
                    </Popover>
                    <Popover
                        content={isVideoOn ? 'Stop video' : 'Start video'}
                        arrow={false}
                        trigger="hover"
                        rootClassName={s.pop}
                    >
                        <div
                            className={`${s.actionItem} ${isVideoOn ? '' : s.actionItemOff}`}
                            onClick={handleClickVideo}
                        >
                            <div className={s.actionItemIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M5 5C4.20435 5 3.44129 5.31607 2.87868 5.87868C2.31607 6.44129 2 7.20435 2 8V16C2 16.7956 2.31607 17.5587 2.87868 18.1213C3.44129 18.6839 4.20435 19 5 19H15C15.7956 19 16.5587 18.6839 17.1213 18.1213C17.6839 17.5587 18 16.7956 18 16V14.414L20.293 16.707C20.4329 16.8468 20.611 16.942 20.805 16.9806C20.9989 17.0192 21.2 16.9993 21.3827 16.9237C21.5654 16.848 21.7215 16.7199 21.8314 16.5555C21.9413 16.391 22 16.1978 22 16V8C22 7.80225 21.9413 7.60895 21.8314 7.44454C21.7215 7.28013 21.5654 7.15199 21.3827 7.07632C21.2 7.00065 20.9989 6.98085 20.805 7.01942C20.611 7.05798 20.4329 7.15319 20.293 7.293L18 9.586V8C18 7.20435 17.6839 6.44129 17.1213 5.87868C16.5587 5.31607 15.7956 5 15 5H5Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <span className={s.closeIcon} />
                            </div>
                            Video
                        </div>
                    </Popover>
                    {!hidePreviewReminderAction && (
                        <div className={s.actionItem} onClick={handleClickSendReminder}>
                            <div className={s.actionItemIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="20"
                                    viewBox="0 0 18 20"
                                    fill="none"
                                >
                                    <path
                                        d="M11.2369 17C12.1019 17 12.5589 18.024 11.9819 18.668C11.6067 19.0871 11.1474 19.4223 10.634 19.6518C10.1205 19.8813 9.56435 20 9.0019 20C8.43945 20 7.88334 19.8813 7.36985 19.6518C6.85636 19.4223 6.39705 19.0871 6.0219 18.668C5.4699 18.052 5.8639 17.089 6.6559 17.007L6.7659 17.001L11.2369 17ZM9.0019 0C10.3599 0 11.5079 0.903 11.8769 2.141L11.9229 2.312L11.9309 2.355C13.0334 2.97671 13.973 3.85086 14.6724 4.9058C15.3719 5.96073 15.8114 7.16641 15.9549 8.424L15.9829 8.711L16.0019 9V11.931L16.0229 12.067C16.1598 12.8038 16.5676 13.4627 17.1659 13.914L17.3329 14.031L17.4949 14.13C18.3549 14.617 18.0549 15.896 17.1179 15.994L17.0019 16H1.0019C-0.0260996 16 -0.3851 14.636 0.5089 14.13C0.88993 13.9144 1.21915 13.618 1.47344 13.2616C1.72773 12.9052 1.90094 12.4974 1.9809 12.067L2.0019 11.924L2.0029 8.954C2.06387 7.64788 2.44406 6.37661 3.11019 5.25147C3.77631 4.12632 4.70807 3.18161 5.8239 2.5L6.0719 2.354L6.0819 2.311C6.22296 1.71283 6.54433 1.17241 7.00255 0.762846C7.46076 0.353278 8.03371 0.0943209 8.6439 0.0209999L8.8259 0.00399995L9.0019 0Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </div>
                            Send reminder
                        </div>
                    )}
                </div>
            )}
            {mode === 'call' && mediaStreamRef.current && mainVideoRef.current && !hideAction && (
                <div className={s.actionWrap}>
                    <Popover
                        content={isAudioOn ? 'Stop audio' : 'Start audo'}
                        arrow={false}
                        trigger="hover"
                        rootClassName={s.pop}
                    >
                        <div
                            className={`${s.actionItem} ${isAudioOn ? '' : s.actionItemOff}`}
                            onClick={handleClickAudio}
                        >
                            <div className={s.actionItemIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="19"
                                    viewBox="0 0 14 19"
                                    fill="none"
                                >
                                    <path
                                        d="M7 12C8.66 12 9.99 10.66 9.99 9L10 3C10 1.34 8.66 0 7 0C5.34 0 4 1.34 4 3V9C4 10.66 5.34 12 7 12ZM12.3 9C12.3 12 9.76 14.1 7 14.1C4.24 14.1 1.7 12 1.7 9H0C0 12.41 2.72 15.23 6 15.72V19H8V15.72C11.28 15.24 14 12.42 14 9H12.3Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <span className={s.closeIcon} />
                            </div>
                            Audio
                        </div>
                    </Popover>
                    <Popover
                        content={isVideoOn ? 'Stop video' : 'Start video'}
                        arrow={false}
                        trigger="hover"
                        rootClassName={s.pop}
                    >
                        <div
                            className={`${s.actionItem} ${isVideoOn ? '' : s.actionItemOff}`}
                            onClick={handleClickVideo}
                        >
                            <div className={s.actionItemIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M5 5C4.20435 5 3.44129 5.31607 2.87868 5.87868C2.31607 6.44129 2 7.20435 2 8V16C2 16.7956 2.31607 17.5587 2.87868 18.1213C3.44129 18.6839 4.20435 19 5 19H15C15.7956 19 16.5587 18.6839 17.1213 18.1213C17.6839 17.5587 18 16.7956 18 16V14.414L20.293 16.707C20.4329 16.8468 20.611 16.942 20.805 16.9806C20.9989 17.0192 21.2 16.9993 21.3827 16.9237C21.5654 16.848 21.7215 16.7199 21.8314 16.5555C21.9413 16.391 22 16.1978 22 16V8C22 7.80225 21.9413 7.60895 21.8314 7.44454C21.7215 7.28013 21.5654 7.15199 21.3827 7.07632C21.2 7.00065 20.9989 6.98085 20.805 7.01942C20.611 7.05798 20.4329 7.15319 20.293 7.293L18 9.586V8C18 7.20435 17.6839 6.44129 17.1213 5.87868C16.5587 5.31607 15.7956 5 15 5H5Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <span className={s.closeIcon} />
                            </div>
                            Video
                        </div>
                    </Popover>
                    <Popover
                        content={isScreenSharing.current ? 'Stop sharing screen' : 'Share screen'}
                        arrow={false}
                        trigger="hover"
                        rootClassName={s.pop}
                    >
                        <div
                            className={`${s.actionItem} ${isScreenSharing.current ? s.actionItemOn : ''}`}
                            onClick={handleClickShare}
                        >
                            <div className={s.actionItemIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M17.625 8.24965H12.75V15.0465C12.75 15.2454 12.671 15.4362 12.5303 15.5769C12.3897 15.7175 12.1989 15.7965 12 15.7965C11.8011 15.7965 11.6103 15.7175 11.4697 15.5769C11.329 15.4362 11.25 15.2454 11.25 15.0465V8.24965H6.375C5.67904 8.2504 5.01179 8.5272 4.51967 9.01932C4.02755 9.51144 3.75074 10.1787 3.75 10.8747V19.8747C3.75074 20.5706 4.02755 21.2379 4.51967 21.73C5.01179 22.2221 5.67904 22.4989 6.375 22.4997H17.625C18.321 22.4989 18.9882 22.2221 19.4803 21.73C19.9725 21.2379 20.2493 20.5706 20.25 19.8747V10.8747C20.2493 10.1787 19.9725 9.51144 19.4803 9.01932C18.9882 8.5272 18.321 8.2504 17.625 8.24965ZM12.75 4.06043L15.2198 6.52981C15.3616 6.66452 15.5505 6.73852 15.746 6.73601C15.9416 6.73351 16.1285 6.65471 16.2668 6.5164C16.4051 6.3781 16.4839 6.19125 16.4864 5.99568C16.4889 5.80011 16.4149 5.61129 16.2802 5.4695L12.5302 1.7195C12.3895 1.57895 12.1988 1.5 12 1.5C11.8012 1.5 11.6105 1.57895 11.4698 1.7195L7.71984 5.4695C7.58513 5.61129 7.51114 5.80011 7.51364 5.99568C7.51614 6.19125 7.59495 6.3781 7.73325 6.5164C7.87155 6.65471 8.0584 6.73351 8.25398 6.73601C8.44955 6.73852 8.63836 6.66452 8.78016 6.52981L11.25 4.06043V8.24965H12.75V4.06043Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </div>
                            Share
                        </div>
                    </Popover>
                    <Popover
                        content={
                            <div className={s.newMessage}>
                                <div>From: {newCommingMessage?.name}</div>
                                <div>{newCommingMessage?.value}</div>
                            </div>
                        }
                        arrow
                        open={!!newCommingMessage}
                        trigger="hover"
                        placement="top"
                        rootClassName={s.pop}
                    >
                        <div className={s.actionItem} onClick={handleClickChat}>
                            <div className={s.actionItemIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M13.629 20.472L13.087 21.388C12.604 22.204 11.397 22.204 10.913 21.388L10.371 20.472C9.951 19.762 9.741 19.406 9.403 19.21C9.065 19.013 8.64 19.006 7.79 18.991C6.534 18.97 5.747 18.893 5.087 18.619C4.48037 18.3677 3.92917 17.9994 3.46487 17.5351C3.00057 17.0708 2.63227 16.5196 2.381 15.913C2 14.995 2 13.83 2 11.5V10.5C2 7.227 2 5.59 2.737 4.388C3.14904 3.7152 3.71445 3.14945 4.387 2.737C5.59 2 7.228 2 10.5 2H13.5C16.773 2 18.41 2 19.613 2.737C20.2854 3.14917 20.8508 3.71456 21.263 4.387C22 5.59 22 7.228 22 10.5V11.5C22 13.83 22 14.995 21.62 15.913C21.3686 16.5197 21.0002 17.071 20.5357 17.5353C20.0712 17.9996 19.5198 18.3678 18.913 18.619C18.253 18.893 17.466 18.969 16.21 18.991C15.36 19.006 14.935 19.013 14.597 19.21C14.259 19.406 14.049 19.761 13.629 20.472ZM8 11.75C7.80109 11.75 7.61032 11.829 7.46967 11.9697C7.32902 12.1103 7.25 12.3011 7.25 12.5C7.25 12.6989 7.32902 12.8897 7.46967 13.0303C7.61032 13.171 7.80109 13.25 8 13.25H13.5C13.6989 13.25 13.8897 13.171 14.0303 13.0303C14.171 12.8897 14.25 12.6989 14.25 12.5C14.25 12.3011 14.171 12.1103 14.0303 11.9697C13.8897 11.829 13.6989 11.75 13.5 11.75H8ZM7.25 9C7.25 8.80109 7.32902 8.61032 7.46967 8.46967C7.61032 8.32902 7.80109 8.25 8 8.25H16C16.1989 8.25 16.3897 8.32902 16.5303 8.46967C16.671 8.61032 16.75 8.80109 16.75 9C16.75 9.19891 16.671 9.38968 16.5303 9.53033C16.3897 9.67098 16.1989 9.75 16 9.75H8C7.80109 9.75 7.61032 9.67098 7.46967 9.53033C7.32902 9.38968 7.25 9.19891 7.25 9Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </div>
                            Chat
                            {newCommingMessage && newCommingMessageAmount && newCommingMessageAmount > 0 && (
                                <span className={s.newMessageAmount}>{newCommingMessageAmount}</span>
                            )}
                        </div>
                    </Popover>

                    {!hideScheduleFollowUpAction && (
                        <div
                            className={s.actionItem}
                            onClick={handleClickScheduleFollowUp}
                            style={{ fontSize: '9px', letterSpacing: '-0.2px' }}
                        >
                            <div className={s.actionItemIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M7.7501 2.5C7.7501 2.30109 7.67108 2.11032 7.53043 1.96967C7.38978 1.82902 7.19901 1.75 7.0001 1.75C6.80119 1.75 6.61042 1.82902 6.46977 1.96967C6.32912 2.11032 6.2501 2.30109 6.2501 2.5V4.08C4.8101 4.195 3.8661 4.477 3.1721 5.172C2.4771 5.866 2.1951 6.811 2.0791 8.25H21.9211C21.8051 6.81 21.5231 5.866 20.8281 5.172C20.1341 4.477 19.1891 4.195 17.7501 4.079V2.5C17.7501 2.30109 17.6711 2.11032 17.5304 1.96967C17.3898 1.82902 17.199 1.75 17.0001 1.75C16.8012 1.75 16.6104 1.82902 16.4698 1.96967C16.3291 2.11032 16.2501 2.30109 16.2501 2.5V4.013C15.5851 4 14.8391 4 14.0001 4H10.0001C9.1611 4 8.4151 4 7.7501 4.013V2.5Z"
                                        fill="white"
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M2 12C2 11.161 2 10.415 2.013 9.75H21.987C22 10.415 22 11.161 22 12V14C22 17.771 22 19.657 20.828 20.828C19.656 21.999 17.771 22 14 22H10C6.229 22 4.343 22 3.172 20.828C2.001 19.656 2 17.771 2 14V12ZM17 14C17.2652 14 17.5196 13.8946 17.7071 13.7071C17.8946 13.5196 18 13.2652 18 13C18 12.7348 17.8946 12.4804 17.7071 12.2929C17.5196 12.1054 17.2652 12 17 12C16.7348 12 16.4804 12.1054 16.2929 12.2929C16.1054 12.4804 16 12.7348 16 13C16 13.2652 16.1054 13.5196 16.2929 13.7071C16.4804 13.8946 16.7348 14 17 14ZM17 18C17.2652 18 17.5196 17.8946 17.7071 17.7071C17.8946 17.5196 18 17.2652 18 17C18 16.7348 17.8946 16.4804 17.7071 16.2929C17.5196 16.1054 17.2652 16 17 16C16.7348 16 16.4804 16.1054 16.2929 16.2929C16.1054 16.4804 16 16.7348 16 17C16 17.2652 16.1054 17.5196 16.2929 17.7071C16.4804 17.8946 16.7348 18 17 18ZM13 13C13 13.2652 12.8946 13.5196 12.7071 13.7071C12.5196 13.8946 12.2652 14 12 14C11.7348 14 11.4804 13.8946 11.2929 13.7071C11.1054 13.5196 11 13.2652 11 13C11 12.7348 11.1054 12.4804 11.2929 12.2929C11.4804 12.1054 11.7348 12 12 12C12.2652 12 12.5196 12.1054 12.7071 12.2929C12.8946 12.4804 13 12.7348 13 13ZM13 17C13 17.2652 12.8946 17.5196 12.7071 17.7071C12.5196 17.8946 12.2652 18 12 18C11.7348 18 11.4804 17.8946 11.2929 17.7071C11.1054 17.5196 11 17.2652 11 17C11 16.7348 11.1054 16.4804 11.2929 16.2929C11.4804 16.1054 11.7348 16 12 16C12.2652 16 12.5196 16.1054 12.7071 16.2929C12.8946 16.4804 13 16.7348 13 17ZM7 14C7.26522 14 7.51957 13.8946 7.70711 13.7071C7.89464 13.5196 8 13.2652 8 13C8 12.7348 7.89464 12.4804 7.70711 12.2929C7.51957 12.1054 7.26522 12 7 12C6.73478 12 6.48043 12.1054 6.29289 12.2929C6.10536 12.4804 6 12.7348 6 13C6 13.2652 6.10536 13.5196 6.29289 13.7071C6.48043 13.8946 6.73478 14 7 14ZM7 18C7.26522 18 7.51957 17.8946 7.70711 17.7071C7.89464 17.5196 8 17.2652 8 17C8 16.7348 7.89464 16.4804 7.70711 16.2929C7.51957 16.1054 7.26522 16 7 16C6.73478 16 6.48043 16.1054 6.29289 16.2929C6.10536 16.4804 6 16.7348 6 17C6 17.2652 6.10536 17.5196 6.29289 17.7071C6.48043 17.8946 6.73478 18 7 18Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </div>
                            Schedule a follow-up
                        </div>
                    )}
                    <Popover
                        content={
                            <div className={s.more}>
                                {mode === 'call' && <p onClick={handleClickParticipants}>Participants</p>}
                                <p onClick={handleClickBackground}>Background</p>
                                <p onClick={handleClickSetting}>Settings</p>
                            </div>
                        }
                        arrow={false}
                        placement="topRight"
                        trigger="hover"
                        rootClassName={s.popMore}
                    >
                        <div className={s.actionItem}>
                            <div className={s.actionItemIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2ZM16.5 10.5C16.1022 10.5 15.7206 10.658 15.4393 10.9393C15.158 11.2206 15 11.6022 15 12C15 12.3978 15.158 12.7794 15.4393 13.0607C15.7206 13.342 16.1022 13.5 16.5 13.5C16.8978 13.5 17.2794 13.342 17.5607 13.0607C17.842 12.7794 18 12.3978 18 12C18 11.6022 17.842 11.2206 17.5607 10.9393C17.2794 10.658 16.8978 10.5 16.5 10.5ZM12 10.5C11.6022 10.5 11.2206 10.658 10.9393 10.9393C10.658 11.2206 10.5 11.6022 10.5 12C10.5 12.3978 10.658 12.7794 10.9393 13.0607C11.2206 13.342 11.6022 13.5 12 13.5C12.3978 13.5 12.7794 13.342 13.0607 13.0607C13.342 12.7794 13.5 12.3978 13.5 12C13.5 11.6022 13.342 11.2206 13.0607 10.9393C12.7794 10.658 12.3978 10.5 12 10.5ZM7.5 10.5C7.10218 10.5 6.72064 10.658 6.43934 10.9393C6.15804 11.2206 6 11.6022 6 12C6 12.3978 6.15804 12.7794 6.43934 13.0607C6.72064 13.342 7.10218 13.5 7.5 13.5C7.89782 13.5 8.27936 13.342 8.56066 13.0607C8.84196 12.7794 9 12.3978 9 12C9 11.6022 8.84196 11.2206 8.56066 10.9393C8.27936 10.658 7.89782 10.5 7.5 10.5Z"
                                        fill="white"
                                    />
                                </svg>
                            </div>
                            More
                        </div>
                    </Popover>
                    <Popover content="End meeting" arrow={false} trigger="hover" rootClassName={s.pop}>
                        <div className={`${s.actionItem} ${s.actionItemEnd}`} onClick={handleClickEnd}>
                            <div className={s.actionItemIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="21"
                                    height="9"
                                    viewBox="0 0 21 9"
                                    fill="none"
                                >
                                    <path
                                        d="M20.7398 7.05004L20.9468 5.91904C21.0435 5.3553 20.9966 4.77629 20.8105 4.23545C20.6243 3.69462 20.3049 3.20938 19.8818 2.82454C17.8238 0.934539 14.6093 -0.00696125 10.2383 3.87454e-05C5.87331 0.00603875 2.75631 0.953539 0.887305 2.84254C0.0953053 3.64204 -0.192695 4.82854 0.129805 5.95504L0.453805 7.08604C0.756805 8.14204 1.77981 8.84854 2.84481 8.73604L4.98081 8.51254C5.42456 8.46854 5.84138 8.27928 6.16658 7.97416C6.49177 7.66903 6.70716 7.2651 6.77931 6.82504L7.19031 4.41904C8.22304 3.93622 9.34462 3.67268 10.4843 3.64504C11.6643 3.60904 12.7453 3.80004 13.7273 4.21804L14.3903 6.78904C14.5064 7.24073 14.7574 7.64632 15.1097 7.95181C15.4621 8.2573 15.8992 8.44819 16.3628 8.49904L18.5108 8.71504C19.0242 8.7729 19.5404 8.63292 19.9542 8.32358C20.3681 8.01425 20.6499 7.55883 20.7398 7.05004Z"
                                        fill="white"
                                    />
                                </svg>
                            </div>
                            End
                        </div>
                    </Popover>
                </div>
            )}
            {/* background config */}
            <Modal
                width={420}
                title="Background"
                open={showBackgroundModal}
                footer={null}
                onCancel={() => {
                    setShowBackgroundModal(false);
                }}
            >
                <div className={s.modalPreview}>
                    {mode === 'preview' && (
                        <div className={s.modalPreviewVideo}>
                            <video-player-container
                                className={s.modalPreviewVideo}
                                style={{
                                    width: '372px',
                                    height: '208px',
                                }}
                            >
                                <video-player
                                    className={s.videoPlayerSmall}
                                    style={{
                                        width: '372px',
                                        height: '208px',
                                    }}
                                    ref={(el) => {
                                        modalVideoRef.current = el;
                                    }}
                                />
                            </video-player-container>
                        </div>
                    )}
                    <div className={s.modalOptions}>
                        {/* <div className={s.modalOptionTitle}>Virtual backgrounds</div> */}
                        <div className={s.modalOption}>
                            <div
                                onClick={() => {
                                    handleSetBackground(0);
                                }}
                                className={`${s.item} ${s.itemNone} ${backgroundItemIndex === 0 ? s.itemSelect : ''}`}
                            >
                                None
                            </div>
                            <div
                                onClick={() => {
                                    handleSetBackground(1);
                                }}
                                className={`${s.item} ${s.itemBlur} ${backgroundItemIndex === 1 ? s.itemSelect : ''}`}
                            >
                                Blur
                            </div>
                            <div
                                onClick={() => {
                                    handleSetBackground(2);
                                }}
                                className={`${s.item} ${backgroundItemIndex === 2 ? s.itemSelect : ''}`}
                            >
                                <img src={VideoImageUrl[0]} width={80} height={50} alt="bg1" />
                            </div>
                            <div
                                onClick={() => {
                                    handleSetBackground(3);
                                }}
                                className={`${s.item} ${backgroundItemIndex === 3 ? s.itemSelect : ''}`}
                            >
                                <img src={VideoImageUrl[1]} width={80} height={50} alt="bg2" />
                            </div>
                            <div
                                onClick={() => {
                                    handleSetBackground(4);
                                }}
                                className={`${s.item} ${backgroundItemIndex === 4 ? s.itemSelect : ''}`}
                            >
                                <img src={VideoImageUrl[2]} width={80} height={50} alt="bg3" />
                            </div>
                            <div
                                onClick={() => {
                                    handleSetBackground(5);
                                }}
                                className={`${s.item} ${backgroundItemIndex === 5 ? s.itemSelect : ''}`}
                            >
                                <img src={VideoImageUrl[3]} width={80} height={50} alt="bg4" />
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
            <VideoSettingModal
                zoomMediaStream={mediaStreamRef.current}
                zoomClient={clientRef.current}
                open={showSettingModal}
                onCancel={() => setShowSettingModal(false)}
                onSpeakerChange={(deviceId) => {
                    //mediaStreamRef.current?.setSpeaker(deviceId);
                }}
                onMicChange={(deviceId) => {
                    //mediaStreamRef.current?.setMic(deviceId);
                }}
                onCameraChange={(deviceId) => {
                    //mediaStreamRef.current?.setCamera(deviceId);
                }}
            />
        </div>
    );
};

export default ZoomVideoView;
